import { memo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { usePortfolioData } from './usePortfolioData';

// Alternating shades of CGIAR green so adjacent bars are visually distinct
const BAR_COLORS = [
  '#2d7f3f', '#3B9B61', '#2d7f3f', '#3B9B61', '#2d7f3f',
  '#3B9B61', '#2d7f3f', '#3B9B61', '#2d7f3f', '#3B9B61',
  '#2d7f3f', '#3B9B61', '#2d7f3f',
];

/**
 * ProgramBreakdownChart
 * Horizontal bar chart showing the number of reported results per CGIAR Science
 * Program (SP01–SP13), sorted from most to fewest results.
 */
export const ProgramBreakdownChart = memo(function ProgramBreakdownChart({ data: dataProp }) {
  const { programBreakdown, filteredResults } = usePortfolioData();
  const yAxisWidth = useResponsiveYAxisWidth(160);

  const data = dataProp ?? programBreakdown;

  if (!data || data.length === 0 || data.every(d => d.count === 0)) {
    return (
      <ChartWrapper title="Results by Program">
        <NoChartData
          message="No program breakdown data available"
          detail="This chart shows result counts per Science Program (SP01–SP13). No results with initiative codes were found in the current selection."
        />
      </ChartWrapper>
    );
  }

  // Filter out programs with zero results for a cleaner chart
  const chartData = data
    .filter(d => d.count > 0)
    .map(d => ({
      name: d.shortName || d.program,
      fullName: d.name,
      count: d.count,
    }));

  const exportData = chartData.map(d => ({ Program: d.fullName, Results: d.count }));

  return (
    <ChartWrapper
      title="Results by Science Program"
      info="Number of reported results per CGIAR Science Program (SP01–SP13), sorted by result count. Only programs with at least one result are shown."
      chartData={exportData}
      sourceResults={filteredResults}
      exportFilename="program-breakdown"
    >
      <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 36)}>
        <BarChart data={chartData} layout="vertical" margin={{ right: 40, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical />
          <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={yAxisWidth}
            tick={({ x, y, payload }) => {
              const entry = chartData.find(d => d.name === payload.value);
              return (
                <g transform={`translate(${x},${y})`}>
                  <title>{entry?.fullName || payload.value}</title>
                  <text
                    x={-4}
                    y={0}
                    dy={4}
                    textAnchor="end"
                    fontSize={yAxisWidth < 160 ? 9 : 11}
                    fill="#555"
                  >
                    {payload.value}
                  </text>
                </g>
              );
            }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <div style={{
                  background: '#fff', padding: '8px 12px', borderRadius: '8px',
                  border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  fontSize: '13px', lineHeight: 1.6,
                }}>
                  <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{d.fullName}</div>
                  <div style={{ color: '#4b5563' }}>
                    Results: <strong>{d.count.toLocaleString()}</strong>
                  </div>
                </div>
              );
            }}
          />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Results">
            {chartData.map((entry, idx) => (
              <Cell key={entry.name} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
            ))}
            <LabelList
              dataKey="count"
              position="right"
              content={({ x, y, width, height, value }) => (
                <text
                  x={x + width + 4}
                  y={y + height / 2}
                  fontSize={11}
                  fill="#666"
                  dominantBaseline="middle"
                >
                  {value}
                </text>
              )}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});
